/**
 * Type definitions for the Decision Graph Editor
 * 
 * These types represent the ReactFlow graph model used for visually editing
 * AST expressions. The graph is converted to/from AstExpression JSON.
 */

import type { Node, Edge } from '@xyflow/react';
import type { EmitRule, ScoreBand } from './decision-definition-schema';

// =============================================================================
// Node Types
// =============================================================================

export type ExpressionNodeType = 
    | 'literal' 
    | 'field' 
    | 'comparison' 
    | 'binary' 
    | 'unary' 
    | 'existence' 
    | 'membership' 
    | 'function' 
    | 'output'
    | 'input'
    | 'hub'
    | 'emitOutput'
    | 'table';

/**
 * Layout direction for handle orientation (matches workflow graph).
 * LR → left/right handles; TB → top/bottom handles.
 */
export type LayoutDirection = "LR" | "TB";

/**
 * Base data interface for all expression nodes
 */
export interface ExpressionNodeDataBase {
    [key: string]: unknown;
    nodeType: ExpressionNodeType;
    label: string;
    /** Current graph layout direction — drives handle side placement */
    layoutDirection?: LayoutDirection;
}

/**
 * Literal node data - represents constant values
 */
export interface LiteralNodeData extends ExpressionNodeDataBase {
    nodeType: 'literal';
    value: string | number | boolean | null;
    valueType: 'string' | 'number' | 'boolean' | 'null';
}

/**
 * Field node data - represents CaseContext field references
 */
export interface FieldNodeData extends ExpressionNodeDataBase {
    nodeType: 'field';
    path: string;
    fieldType?: 'string' | 'number' | 'boolean' | 'date' | 'array' | 'object';
}

/**
 * Comparison node data - represents comparison operations
 */
export interface ComparisonNodeData extends ExpressionNodeDataBase {
    nodeType: 'comparison';
    operator: '==' | '!=' | '<' | '<=' | '>' | '>=';
}

/**
 * Binary node data - represents AND/OR operations
 */
export interface BinaryNodeData extends ExpressionNodeDataBase {
    nodeType: 'binary';
    operator: 'AND' | 'OR';
}

/**
 * Unary node data - represents NOT operation
 */
export interface UnaryNodeData extends ExpressionNodeDataBase {
    nodeType: 'unary';
    operator: 'NOT';
}

/**
 * Existence node data - represents IS_SET/EXISTS checks
 */
export interface ExistenceNodeData extends ExpressionNodeDataBase {
    nodeType: 'existence';
    operator: 'IS_SET' | 'EXISTS';
}

/**
 * Membership node data - represents IN/NOT_IN set checks
 */
export interface MembershipNodeData extends ExpressionNodeDataBase {
    nodeType: 'membership';
    operator: 'IN' | 'NOT_IN';
    /** Number of set element handles to render */
    setSize: number;
}

/**
 * Function node data - represents custom function calls
 */
export interface FunctionNodeData extends ExpressionNodeDataBase {
    nodeType: 'function';
    functionName: string;
    /** Number of argument handles to render */
    argumentCount: number;
}

/**
 * Output node data - represents the expression result
 */
export interface OutputNodeData extends ExpressionNodeDataBase {
    nodeType: 'output';
    outputType: 'boolean' | 'value';
}

/**
 * Input node data - entry point where facts enter the decision graph
 */
export interface InputNodeData extends ExpressionNodeDataBase {
    nodeType: 'input';
    inputSchemaRef?: string;
    factPaths: string[];
}

/**
 * Hub node data - central decision point where all logic converges
 */
export interface HubNodeData extends ExpressionNodeDataBase {
    nodeType: 'hub';
    decisionMode: 'boolean' | 'tri_state' | 'multi_outcome' | 'scorecard';
    /** Emit rules used to build multi-outcome handles (mirrored from definition). */
    emitRules?: EmitRule[];
    /** Score bands for scorecard mode — each band becomes an output handle. */
    scoreBands?: ScoreBand[];
}

/**
 * Emit output node data - one per emit rule, feeds from hub
 */
export interface EmitOutputNodeData extends ExpressionNodeDataBase {
    nodeType: 'emitOutput';
    emitRuleId: string;
    outcome?: string;
    stopProcessing: boolean;
}

/**
 * Table node data - represents a table-compatible subtree
 */
export interface TableNodeData extends ExpressionNodeDataBase {
    nodeType: 'table';
    ruleElementId?: string;
    rowCount: number;
    columnCount: number;
}

/**
 * Union of all node data types
 */
export type ExpressionNodeData = 
    | LiteralNodeData 
    | FieldNodeData 
    | ComparisonNodeData 
    | BinaryNodeData 
    | UnaryNodeData 
    | ExistenceNodeData 
    | MembershipNodeData 
    | FunctionNodeData 
    | OutputNodeData
    | InputNodeData
    | HubNodeData
    | EmitOutputNodeData
    | TableNodeData;

// =============================================================================
// Edge Types
// =============================================================================

export type ExpressionEdgeType = 'value' | 'boolean';

/**
 * Edge data for expression graph edges
 */
export interface ExpressionEdgeData {
    [key: string]: unknown;
    edgeType: ExpressionEdgeType;
    sourcePort?: string;
    targetPort?: string;
}

// =============================================================================
// ReactFlow Node/Edge Types
// =============================================================================

export type ExpressionNode = Node<ExpressionNodeData, ExpressionNodeType>;
export type ExpressionEdge = Edge<ExpressionEdgeData>;

// =============================================================================
// Conversion Types
// =============================================================================

/**
 * Result of converting AST to ReactFlow graph
 */
export interface AstToFlowConversionResult {
    nodes: ExpressionNode[];
    edges: ExpressionEdge[];
    outputNodeId: string;
}

/**
 * Result of exporting ReactFlow graph to AST
 */
export interface FlowToAstExportResult {
    valid: boolean;
    expression?: import('@/types/decision-editor').AstExpression;
    errors: string[];
}

// =============================================================================
// Validation Types
// =============================================================================

/**
 * Result of validating a connection
 */
export interface ConnectionValidationResult {
    valid: boolean;
    reason?: string;
}

/**
 * Result of validating the entire graph
 */
export interface GraphValidationResult {
    valid: boolean;
    errors: string[];
    warnings: string[];
}

// =============================================================================
// Layout Configuration
// =============================================================================

/**
 * Node dimensions for layout calculations
 */
export const EXPRESSION_NODE_DIMENSIONS = {
    width: 140,
    height: 60,
    spacingX: 200,
    spacingY: 80,
} as const;

/**
 * Handle port definitions for each node type
 */
export const HANDLE_TYPES: Record<ExpressionNodeType, Record<string, 'boolean' | 'value' | 'any'>> = {
    literal: { result: 'value' },
    field: { result: 'value' },
    comparison: { left: 'value', right: 'value', result: 'boolean' },
    binary: { left: 'boolean', right: 'boolean', result: 'boolean' },
    unary: { operand: 'boolean', result: 'boolean' },
    existence: { operand: 'value', result: 'boolean' },
    membership: { operand: 'value', result: 'boolean' }, // set handles are dynamic
    function: { result: 'value' }, // arg handles are dynamic
    output: { input: 'boolean' },
    input: { result: 'any' },
    hub: { input: 'boolean', yes: 'boolean', no: 'boolean', unknown: 'boolean' },
    emitOutput: { input: 'boolean' },
    table: { input: 'value', result: 'boolean' },
};
